/* =========================================================================
   telemetry-time-range.utils.ts — Timeframe definitions, UTC ISO parsers,
   and in-memory time range verification utilities for Telemetry Dashboard.
   ========================================================================= */

import { getActiveTelemetrySeriesStore } from './telemetry-helpers.utils';

export interface TimeframeOption {
  label: string;
  minutes: number;
  seconds: number;
}

export const TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { label: '5 Mins', minutes: 5, seconds: 300 },
  { label: '15 Mins', minutes: 15, seconds: 900 },
  { label: '30 Mins', minutes: 30, seconds: 1800 },
  { label: '1 Hr', minutes: 60, seconds: 3600 },
  { label: '3 Hrs', minutes: 180, seconds: 10800 },
  { label: '6 Hrs', minutes: 360, seconds: 21600 },
  { label: '12 Hrs', minutes: 720, seconds: 43200 },
  { label: '24 Hrs', minutes: 1440, seconds: 86400 },
];

/**
 * Returns user-friendly timeframe label for a given minutes value.
 */
export function getTimeframeLabel(minutes: number): string {
  const opt = TIMEFRAME_OPTIONS.find((o) => o.minutes === minutes);
  if (opt) return opt.label;
  if (minutes >= 60) {
    const hrs = minutes / 60;
    if (hrs === 1) return '1 Hr';
    return Number.isInteger(hrs) ? `${hrs} Hrs` : `${minutes} Mins`;
  }
  return `${minutes} Mins`;
}

/**
 * Returns the list of timeframe options filtered by simulation duration.
 * Includes all timeframe options smaller than or equal to the duration,
 * plus the first enclosing timeframe bucket that covers the full duration.
 */
export function getFilteredTimeframeOptions(
  simulationDurationMinutes?: number | null
): TimeframeOption[] {
  const maxMinutes = (simulationDurationMinutes != null && simulationDurationMinutes > 0)
    ? simulationDurationMinutes
    : 24 * 60;

  const allowed: TimeframeOption[] = [];
  for (const opt of TIMEFRAME_OPTIONS) {
    if (opt.minutes <= maxMinutes) {
      allowed.push(opt);
      if (opt.minutes === maxMinutes) break;
    } else {
      // First option greater than maxMinutes is the enclosing bucket (e.g. 60 min for a 44 min simulation)
      allowed.push(opt);
      break;
    }
  }
  return allowed.length > 0 ? allowed : [TIMEFRAME_OPTIONS[0]];
}

/**
 * Calculates the best-fitting initial timeframe in minutes based on elapsed simulation time.
 * - elapsed <= 5 min -> 5m
 * - 5 < elapsed <= 15 min -> 15m (e.g. 8 min -> 15m)
 * - 15 < elapsed <= 30 min -> 30m
 * - elapsed > 30 min -> 60m (max initial baseline cap)
 */
export function calculateOptimalInitialTimeframe(
  simStartSec?: number | null,
  currentSimSec?: number | null
): number {
  if (!simStartSec || !currentSimSec || currentSimSec <= simStartSec) {
    return 60; // Default fallback
  }
  const elapsedMinutes = (currentSimSec - simStartSec) / 60;
  if (elapsedMinutes <= 5) return 5;
  if (elapsedMinutes <= 15) return 15;
  if (elapsedMinutes <= 30) return 30;
  return 60;
}

/**
 * Resolves the effective simulation duration in minutes for both summary and live views.
 *
 * @param isSummaryView Whether the scenario is in completed / summary mode
 * @param simStartSec Scenario simulation start epoch in seconds
 * @param simTimeAbsSec Current/completed absolute simulation time epoch in seconds
 * @param liveDurationMinutes Stored simulation duration in minutes from deployment details / scenario
 * @returns Effective duration in minutes (defaults to 1440 min / 24 hr)
 */
export function resolveEffectiveSimulationDurationMinutes(
  isSummaryView: boolean,
  simStartSec?: number | null,
  simTimeAbsSec?: number | null,
  liveDurationMinutes?: number | null
): number {
  const DEFAULT_DURATION_MINUTES = 24 * 60;

  if (isSummaryView && simStartSec != null && simTimeAbsSec != null && simTimeAbsSec > simStartSec) {
    const elapsedMinutes = Math.floor((simTimeAbsSec - simStartSec) / 60);
    return elapsedMinutes > 0 ? elapsedMinutes : DEFAULT_DURATION_MINUTES;
  }

  const liveMinutes = Number(liveDurationMinutes);
  return Number.isFinite(liveMinutes) && liveMinutes > 0 ? liveMinutes : DEFAULT_DURATION_MINUTES;
}

/**
 * Parses a UTC ISO string (e.g. "2025-01-12T14:34:23Z" or "2025-01-12T14:34:23") to epoch seconds.
 * Returns null if invalid.
 */
export function parseUtcIsoToSec(isoString: string | null | undefined): number | null {
  if (!isoString || typeof isoString !== 'string') return null;
  const trimmed = isoString.trim();
  if (!trimmed) return null;

  // Ensure UTC parsing by appending Z if no timezone offset is provided
  let formatted = trimmed;
  if (!formatted.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(formatted)) {
    formatted = `${formatted}Z`;
  }

  const timestampMs = Date.parse(formatted);
  if (isNaN(timestampMs)) return null;

  return Math.floor(timestampMs / 1000);
}

/**
 * Formats epoch seconds to standard UTC ISO string (e.g. "2025-01-12T14:34:23Z").
 */
export function formatSecToUtcIso(sec: number | null | undefined): string {
  if (sec == null || isNaN(sec) || sec <= 0) return '';
  const date = new Date(sec * 1000);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Inspects in-memory telemetry series store and returns loaded min and max timestamps in seconds.
 * Ignores stale / inactive channels that stopped receiving data long before the latest telemetry
 * to prevent historical window calculations from being poisoned by outlier timestamps.
 */
export function getInMemoryTimeRange(): { minSec: number; maxSec: number } | null {
  const store = getActiveTelemetrySeriesStore();
  if (!store || store.length === 0) return null;

  // 1. Identify global latest timestamp across all series
  let globalMaxMs = 0;
  for (const s of store) {
    if (s && s.data && Array.isArray(s.data) && s.data.length > 0) {
      const last = s.data[s.data.length - 1]?.value?.[0];
      if (typeof last === 'number' && last > globalMaxMs) {
        globalMaxMs = last;
      }
    }
  }

  if (globalMaxMs <= 0) return null;

  // 2. Identify active / recent series
  // Channels whose latest data is more than 2 hours behind globalMaxMs are considered stale / disconnected
  // and should not poison the in-memory historical boundary calculation for active channels.
  const activeFirstTimestamps: number[] = [];
  const allFirstTimestamps: number[] = [];

  for (const s of store) {
    if (s && s.data && Array.isArray(s.data) && s.data.length > 0) {
      const first = s.data[0]?.value?.[0];
      const last = s.data[s.data.length - 1]?.value?.[0];
      if (typeof first === 'number' && first > 0) {
        allFirstTimestamps.push(first);
        if (typeof last === 'number' && globalMaxMs - last <= 2 * 3600 * 1000) {
          activeFirstTimestamps.push(first);
        }
      }
    }
  }

  const candidateTimestamps = activeFirstTimestamps.length > 0 ? activeFirstTimestamps : allFirstTimestamps;
  if (candidateTimestamps.length === 0) return null;

  // Sort candidate timestamps in ascending order
  candidateTimestamps.sort((a, b) => a - b);

  // Use the 75th percentile of first timestamps to ensure the majority of active channels
  // have data across the calculated window, preventing single early outliers from skipping API fetches.
  const p75Idx = Math.floor(candidateTimestamps.length * 0.75);
  const effectiveMinMs = candidateTimestamps[Math.min(p75Idx, candidateTimestamps.length - 1)];

  return {
    minSec: Math.floor(effectiveMinMs / 1000),
    maxSec: Math.floor(globalMaxMs / 1000),
  };
}

/**
 * Checks if the specified time range [startSec, endSec] is already covered by in-memory telemetry data.
 * Uses a 5-second tolerance for boundary rounding.
 */
export function isTimeRangeInMemory(startSec: number, endSec: number): boolean {
  if (startSec >= endSec) return true;
  const mem = getInMemoryTimeRange();
  if (!mem) return false;

  // Covered if in-memory min <= target start and in-memory max >= target end
  return mem.minSec <= startSec + 5 && mem.maxSec >= endSec - 5;
}

/**
 * Calculates missing time slice when expanding historical or future timeframe window.
 * Returns null if the target range is already satisfied.
 */
export function getMissingTimeRange(
  targetStartSec: number,
  targetEndSec: number
): { startSec: number; endSec: number } | null {
  if (targetStartSec >= targetEndSec) return null;
  const mem = getInMemoryTimeRange();

  if (!mem) {
    return { startSec: targetStartSec, endSec: targetEndSec };
  }

  // If already covered, nothing missing
  if (mem.minSec <= targetStartSec + 5 && mem.maxSec >= targetEndSec - 5) {
    return null;
  }

  // If missing on the historical left side:
  if (targetStartSec < mem.minSec - 5) {
    return {
      startSec: targetStartSec,
      endSec: Math.min(targetEndSec, mem.minSec),
    };
  }

  // If missing entirely outside current window:
  return { startSec: targetStartSec, endSec: targetEndSec };
}
